import {DriveComment, DriveFile, DriveDriveInfo, DriveReply, DriveRevision, FOLDER_MIME_TYPE} from './types';
import {stringifyJson} from '../domain/json';

const API_BASE = 'https://www.googleapis.com/drive/v3';
const UPLOAD_BASE = 'https://www.googleapis.com/upload/drive/v3';

// Only the image metadata the viewer's info panel shows — the whole object is much larger per photo.
const IMAGE_METADATA_FIELDS = 'width,height,rotation,time,cameraMake,cameraModel,lens,exposureTime,aperture,isoSpeed,focalLength,location';
const FILE_FIELDS = `id,name,mimeType,parents,thumbnailLink,size,createdTime,modifiedTime,version,owners(displayName),capabilities(canDelete),imageMediaMetadata(${IMAGE_METADATA_FIELDS})`;

// The comments endpoints reject requests without an explicit field mask.
const USER_FIELDS = 'displayName,photoLink,me';
const REPLY_FIELDS = `id,content,createdTime,deleted,action,author(${USER_FIELDS})`;
const COMMENT_FIELDS = `id,content,anchor,createdTime,resolved,deleted,author(${USER_FIELDS}),replies(${REPLY_FIELDS})`;
const REVISION_FIELDS = `id,modifiedTime,mimeType,originalFilename,size,keepForever,lastModifyingUser(${USER_FIELDS})`;

export class DriveApiError extends Error {
    status: number;

    constructor(status: number, message: string) {
        super(message);
        this.name = 'DriveApiError';
        this.status = status;
    }
}

/** Maps a Drive API error to a short, user-facing message for toasts. */
export function describeDriveError(error: unknown): string {
    if (error instanceof DriveApiError) {
        if (error.status === 401) return 'A munkameneted lejárt. Jelentkezz be újra.';
        if (error.status === 403) return 'Nincs jogosultságod ehhez a művelethez.';
        if (error.status === 404) return 'Ez a fájl vagy mappa már nem létezik.';
        if (error.status === 429) return 'A Google Drive korlátozza a kéréseket — próbáld újra hamarosan.';
        return error.message || 'A Google Drive-kérés sikertelen.';
    }
    if (error instanceof TypeError)
        return 'Hálózati hiba történt. Ellenőrizd az internetkapcsolatot!';
    if (error instanceof Error) return error.message;
    return 'Valami hiba történt.';
}

async function driveFetch(accessToken: string, url: string, init?: RequestInit): Promise<Response> {
    const response = await fetch(url, {
        ...init,
        headers: {
            ...(init?.headers ?? {}),
            Authorization: `Bearer ${accessToken}`,
        },
    });
    if (!response.ok) {
        let message = response.statusText;
        try {
            const body = await response.json();
            message = body.error?.message ?? message;
        } catch {
            // response body wasn't JSON — keep the status text
        }
        throw new DriveApiError(response.status, message);
    }
    return response;
}

export async function listSharedDrives(accessToken: string): Promise<DriveDriveInfo[]> {
    const drives: DriveDriveInfo[] = [];
    let pageToken: string | undefined;
    do {
        const params = new URLSearchParams({
            pageSize: '100',
            fields: 'nextPageToken,drives(id,name)',
        });
        if (pageToken) params.set('pageToken', pageToken);
        const response = await driveFetch(accessToken, `${API_BASE}/drives?${params.toString()}`);
        const data = await response.json();
        drives.push(...(data.drives ?? []));
        pageToken = data.nextPageToken;
    } while (pageToken);
    return drives;
}

/** Runs a `files.list` search inside one shared drive, following every result page. */
async function searchDrive(accessToken: string, driveId: string, q: string, fileFields: string, orderBy?: string): Promise<DriveFile[]> {
    const files: DriveFile[] = [];
    let pageToken: string | undefined;
    do {
        const params = new URLSearchParams({
            q,
            corpora: 'drive',
            driveId,
            includeItemsFromAllDrives: 'true',
            supportsAllDrives: 'true',
            pageSize: '1000',
            fields: `nextPageToken,files(${fileFields})`,
        });
        if (orderBy) params.set('orderBy', orderBy);
        if (pageToken) params.set('pageToken', pageToken);
        const response = await driveFetch(accessToken, `${API_BASE}/files?${params.toString()}`);
        const data = await response.json();
        files.push(...(data.files ?? []));
        pageToken = data.nextPageToken;
    } while (pageToken);
    return files;
}

/** Quotes a value for a Drive search query. */
function quoteQueryValue(value: string): string {
    return `'${value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
}

/** Lists the direct children of a folder (or a shared drive's root, since driveId === root folder id). */
export function listFilesInFolder(accessToken: string, driveId: string, folderId: string): Promise<DriveFile[]> {
    return searchDrive(accessToken, driveId, `${quoteQueryValue(folderId)} in parents and trashed = false`, FILE_FIELDS, 'name');
}

/** Every non-folder file with exactly this name anywhere in a shared drive — one search instead of a listing per folder. */
export function listFilesByName(accessToken: string, driveId: string, name: string): Promise<DriveFile[]> {
    const q = `name = ${quoteQueryValue(name)} and mimeType != '${FOLDER_MIME_TYPE}' and trashed = false`;
    return searchDrive(accessToken, driveId, q, 'id,name,mimeType,parents,modifiedTime,version');
}

/** The non-folder files with exactly this name in one folder (e.g. an album's cover in its web bin), without listing the folder. */
export function listFilesInFolderByName(accessToken: string, driveId: string, folderId: string, name: string): Promise<DriveFile[]> {
    const q = `${quoteQueryValue(folderId)} in parents and name = ${quoteQueryValue(name)} and mimeType != '${FOLDER_MIME_TYPE}' and trashed = false`;
    return searchDrive(accessToken, driveId, q, FILE_FIELDS);
}

export async function getFileMediaBlob(accessToken: string, fileId: string): Promise<Blob> {
    const response = await driveFetch(accessToken, `${API_BASE}/files/${fileId}?alt=media&supportsAllDrives=true`);
    return response.blob();
}

/** The content of one stored version of a file (see `listRevisions`). */
export async function getRevisionMediaBlob(accessToken: string, fileId: string, revisionId: string): Promise<Blob> {
    const response = await driveFetch(accessToken, `${API_BASE}/files/${fileId}/revisions/${revisionId}?alt=media`);
    return response.blob();
}

/** Fetches only the first `endByte` bytes of a file's content via an HTTP Range request. */
export async function getFileMediaRange(accessToken: string, fileId: string, endByte: number): Promise<ArrayBuffer> {
    const response = await driveFetch(accessToken, `${API_BASE}/files/${fileId}?alt=media&supportsAllDrives=true`, {
        headers: {Range: `bytes=0-${endByte}`},
    });
    return response.arrayBuffer();
}

export async function getFileMediaText(accessToken: string, fileId: string): Promise<string> {
    const response = await driveFetch(accessToken, `${API_BASE}/files/${fileId}?alt=media&supportsAllDrives=true`);
    return response.text();
}

export async function createFolder(accessToken: string, name: string, parentId: string): Promise<DriveFile> {
    const params = new URLSearchParams({supportsAllDrives: 'true', fields: FILE_FIELDS});
    const response = await driveFetch(accessToken, `${API_BASE}/files?${params.toString()}`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({name, mimeType: FOLDER_MIME_TYPE, parents: [parentId]}),
    });
    return response.json();
}

export async function createJsonFile(accessToken: string, name: string, parentId: string, content: unknown, beautify = false): Promise<DriveFile> {
    const boundary = 'spot_drive_' + Math.random().toString(36).slice(2);
    const metadata = {name, parents: [parentId], mimeType: 'application/json'};
    const body =
        `--${boundary}\r\n` +
        'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
        `${JSON.stringify(metadata)}\r\n` +
        `--${boundary}\r\n` +
        'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
        `${stringifyJson(content, beautify)}\r\n` +
        `--${boundary}--`;
    const params = new URLSearchParams({uploadType: 'multipart', supportsAllDrives: 'true', fields: FILE_FIELDS});
    const response = await driveFetch(accessToken, `${UPLOAD_BASE}/files?${params.toString()}`, {
        method: 'POST',
        headers: {'Content-Type': `multipart/related; boundary=${boundary}`},
        body,
    });
    return response.json();
}

/** Overwrites a JSON file's content; returns its updated metadata (including the new version). */
export async function updateJsonFileContent(accessToken: string, fileId: string, content: unknown, beautify = false): Promise<DriveFile> {
    const params = new URLSearchParams({uploadType: 'media', supportsAllDrives: 'true', fields: FILE_FIELDS});
    const response = await driveFetch(accessToken, `${UPLOAD_BASE}/files/${fileId}?${params.toString()}`, {
        method: 'PATCH',
        headers: {'Content-Type': 'application/json'},
        body: stringifyJson(content, beautify),
    });
    return response.json();
}

/** Metadata-only fetch (no content), used e.g. to resolve a breadcrumb segment's display name. */
export async function getFile(accessToken: string, fileId: string, fields: string = FILE_FIELDS): Promise<DriveFile> {
    const params = new URLSearchParams({supportsAllDrives: 'true', fields});
    const response = await driveFetch(accessToken, `${API_BASE}/files/${fileId}?${params.toString()}`);
    return response.json();
}

export async function renameFile(accessToken: string, fileId: string, newName: string): Promise<DriveFile> {
    const params = new URLSearchParams({supportsAllDrives: 'true', fields: FILE_FIELDS});
    const response = await driveFetch(accessToken, `${API_BASE}/files/${fileId}?${params.toString()}`, {
        method: 'PATCH',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({name: newName}),
    });
    return response.json();
}

export async function moveFile(accessToken: string, fileId: string, addParentId: string, removeParentId: string): Promise<void> {
    const params = new URLSearchParams({addParents: addParentId, removeParents: removeParentId, supportsAllDrives: 'true'});
    await driveFetch(accessToken, `${API_BASE}/files/${fileId}?${params.toString()}`, {method: 'PATCH'});
}

/**
 * Server-side copy into a folder (also its own); the original is left in place. The name is passed
 * explicitly so the copy never gets a "Copy of" prefix.
 */
export async function copyFile(accessToken: string, fileId: string, name: string, parentId: string): Promise<DriveFile> {
    const params = new URLSearchParams({supportsAllDrives: 'true', fields: FILE_FIELDS});
    const response = await driveFetch(accessToken, `${API_BASE}/files/${fileId}/copy?${params.toString()}`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({name, parents: [parentId]}),
    });
    return response.json();
}

export async function deleteFile(accessToken: string, fileId: string): Promise<void> {
    const params = new URLSearchParams({supportsAllDrives: 'true'});
    await driveFetch(accessToken, `${API_BASE}/files/${fileId}?${params.toString()}`, {method: 'DELETE'});
}

/** Lists every (non-deleted) comment thread on a file, resolved ones included. */
export async function listComments(accessToken: string, fileId: string): Promise<DriveComment[]> {
    const comments: DriveComment[] = [];
    let pageToken: string | undefined;
    do {
        const params = new URLSearchParams({
            pageSize: '100',
            fields: `nextPageToken,comments(${COMMENT_FIELDS})`,
        });
        if (pageToken) params.set('pageToken', pageToken);
        const response = await driveFetch(accessToken, `${API_BASE}/files/${fileId}/comments?${params.toString()}`);
        const data = await response.json();
        comments.push(...(data.comments ?? []));
        pageToken = data.nextPageToken;
    } while (pageToken);
    return comments;
}

/** Every stored version of a file's content, oldest first; the last one is the current content. */
export async function listRevisions(accessToken: string, fileId: string): Promise<DriveRevision[]> {
    const revisions: DriveRevision[] = [];
    let pageToken: string | undefined;
    do {
        const params = new URLSearchParams({
            pageSize: '200',
            fields: `nextPageToken,revisions(${REVISION_FIELDS})`,
        });
        if (pageToken) params.set('pageToken', pageToken);
        const response = await driveFetch(accessToken, `${API_BASE}/files/${fileId}/revisions?${params.toString()}`);
        const data = await response.json();
        revisions.push(...(data.revisions ?? []));
        pageToken = data.nextPageToken;
    } while (pageToken);
    return revisions;
}

/** `anchor` is an opaque JSON string to Drive, e.g. a region of a picture (`formatRectAnchor`). */
export async function createComment(accessToken: string, fileId: string, content: string, anchor?: string): Promise<DriveComment> {
    const params = new URLSearchParams({fields: COMMENT_FIELDS});
    const response = await driveFetch(accessToken, `${API_BASE}/files/${fileId}/comments?${params.toString()}`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(anchor ? {content, anchor} : {content}),
    });
    return response.json();
}

/** Adds a reply to a comment thread; a reply with `action: 'resolve'` (content optional) resolves the thread. */
export async function createReply(
    accessToken: string,
    fileId: string,
    commentId: string,
    reply: { content?: string; action?: 'resolve' | 'reopen' },
): Promise<DriveReply> {
    const params = new URLSearchParams({fields: REPLY_FIELDS});
    const response = await driveFetch(accessToken, `${API_BASE}/files/${fileId}/comments/${commentId}/replies?${params.toString()}`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(reply),
    });
    return response.json();
}
